import { useMemo, useState, useCallback } from 'react';
import { ColumnTable, Table, Button } from '@octano/global-ui';
import { useTranslation } from 'react-i18next';
import OfferModal from './AdmissionProcessOffers/AdmissionProcessOfferModal';
import { Offer } from '../types';
import { getSelectedAdmissionScope } from '../utils';

interface Props {
  offers: Offer[];
  availableOffers: any[];
  processFirstInstallmentRuleId?: number;
  refresh: () => void;
}

const FIRST_INSTALLMENT_RULE_MAPPING: Record<string, string> = {
  SPECIFIC_DATE: 'specificDate',
  SPECIFIC_MONTH_DAY: 'firstDayOfSpecificMonth',
  NEXT_MONTH: 'nextMonth',
  NEXT_MONTH_DAY: 'nextMonthDay',
};

export default function ProcessOffersList({
  offers,
  availableOffers,
  processFirstInstallmentRuleId,
  refresh,
}: Props) {
  const { t } = useTranslation('academic-offer/admission-processes');
  const [selectedOffer, setSelectedOffer] = useState<Offer | undefined>(
    undefined,
  );
  const [showModal, setShowModal] = useState(false);

  const toggleModal = useCallback((offer?: Offer) => {
    setSelectedOffer(offer);
    setShowModal((prev) => !prev);
  }, []);

  const columns = useMemo<ColumnTable<any>[]>(
    () => [
      {
        headerText: t('offersTable.studyPlan'),
        columnName: 'studyPlanName',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        headerText: t('offersTable.campus'),
        columnName: 'campusName',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        headerText: t('offersTable.schedule'),
        columnName: 'scheduleName',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        headerText: t('offersTable.quota'),
        columnName: 'quota',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        headerText: t('offersTable.startDate'),
        columnName: 'startDate',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        headerText: t('offersTable.duration'),
        columnName: 'duration',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        headerText: t('offersTable.enrollmentType'),
        columnName: 'admissionScopes',
        thClassName: 'text-center max-w-200',
        tdClassName: 'text-center max-w-200',
        cellFormat: ({ row }: any) => {
          if (!row.admissionScopes || row.admissionScopes.length === 0)
            return '-';
          const scope = getSelectedAdmissionScope(row.admissionScopes, t);
          return scope?.label || '-';
        },
      },
      {
        headerText: t('offersTable.maxInstallments'),
        columnName: 'maxInstallments',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        headerText: t('offersTable.firstInstallmentRule'),
        columnName: 'firstInstallmentRule',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }: any) => {
          if (!row.firstInstallmentRule) return '-';
          const code = row.firstInstallmentRule.code;
          const translationKey =
            FIRST_INSTALLMENT_RULE_MAPPING[code] || code.toLowerCase();
          return t(
            `processList.modalForm.firstExpirationRuleOptions.${translationKey}`,
          );
        },
      },
      {
        headerText: t('offersTable.actions'),
        columnName: 'actions',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }: any) => (
          <Button
            text={t('offersTable.edit')}
            size="sm"
            outlined
            onClick={() => toggleModal(row)}
          />
        ),
      },
    ],
    [t, toggleModal],
  );

  return (
    <>
      <div className="mt-5 mb-3">
        <div>
          <div className="fs-18 font-weight-bold text-primary text-uppercase">
            {t('management.offers')}
          </div>
          <p className="fs-16 mb-0">{t('management.offersDescription')}</p>
        </div>
      </div>
      <Table columns={columns} data={offers} />
      {showModal && (
        <OfferModal
          toggle={() => toggleModal()}
          offer={selectedOffer}
          availableOffers={availableOffers}
          processFirstInstallmentRuleId={processFirstInstallmentRuleId}
          refresh={refresh}
        />
      )}
      <div className="d-flex justify-content-end mt-3">
        <Button
          text={t('offersTable.addOffer')}
          size="md"
          className="px-5 font-weight-bold mt-3"
          onClick={() => toggleModal()}
        />
      </div>
    </>
  );
}
